import React, { useState, useEffect } from 'react';
import ConnectWallet from '../components/ConnectWallet';
import { useGRPCConnector } from '../utils/gRPCConnector';
import { userService } from '../services/userService';
import { useWalletBalances } from '../services/walletBalanceService';
import { 
  Wallet, AlertTriangle, Lock, Check,
  Zap, Crown, Shield, Star, ArrowRight
} from 'lucide-react';

interface AccessProps {
  onGoHome: () => void;
  onLaunchPlatform: () => void;
  walletAddress?: string;
  walletProvider?: 'phantom' | 'solflare';
  onShowConnectModal?: () => void;
  onDisconnectWallet?: () => void;
}

// Simplified tier information
const tiers = [
  {
    name: "V2 BETA Access",
    description: "ALL EKKO TOOLS",
    icon: <Zap size={18} />,
    available: true,
    features: [
            "0% Fees",
            "All Feature Access"
    ]
  },
  {
    name: "Tier 1",
    description: "BASIC TOOLKIT",
    icon: <Shield size={18} />,
    available: false,
    features: [
      "Whale Tracker",
      "Rug Tracker",
      "Advanced Auto Trader",
      "0.5% Fees"
    ]
  },
  {
    name: "Tier 2",
    description: "PRO ACCESS",
    icon: <Star size={18} />,
    available: false,
    features: [
      "Copy Trader",
      "Sniper Bot",
      "Copy Trade Pools",
      "0.2% Fees"
    ]
  },
  {
    name: "ELITE",
    description: "ALL ACCESS",
    icon: <Crown size={18} />,
    available: false,
    features: [
      "All Feature Access",
      "Multi-Wallet Trading",
      "X-Tracker",
      "ELITE Group Access",
      "0% Fees"
    ]
  }
];

const Access: React.FC<AccessProps> = ({ 
  onGoHome, 
  onLaunchPlatform, 
  walletAddress: externalWalletAddress, 
  walletProvider: externalWalletProvider,
  onShowConnectModal: externalShowConnectModal,
  onDisconnectWallet: externalDisconnectWallet
}) => {
  // Logo URL
  const EKKO_LOGO = "https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/EkkoLogo3%20(2).png";
  
  // Use internal state only if external state is not provided
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [walletAddress, setWalletAddress] = useState<string | null>(externalWalletAddress || null);
  const [walletProvider, setWalletProvider] = useState<'phantom' | 'solflare' | null>(externalWalletProvider || null);
  const [userId, setUserId] = useState<string | null>(null);
  const [activeTier, setActiveTier] = useState<number>(0);
  
  // Update internal state when external state changes
  useEffect(() => {
    if (externalWalletAddress) {
      setWalletAddress(externalWalletAddress);
    }
    if (externalWalletProvider) {
      setWalletProvider(externalWalletProvider);
    }
  }, [externalWalletAddress, externalWalletProvider]);

  const { subscribeToAddress, unsubscribeFromAddress, isConnected } = useGRPCConnector();
  const { refreshBalances, setWallet } = useWalletBalances();

  // Use external wallet connection if available, otherwise use internal state
  const handleShowConnectModal = () => {
    if (externalShowConnectModal) {
      externalShowConnectModal();
    } else {
      setShowConnectModal(true);
    }
  };

  // Use external wallet disconnection if available, otherwise use internal method
  const handleDisconnectWallet = async () => {
    if (externalDisconnectWallet) {
      externalDisconnectWallet();
      
      // Reset local state as well to ensure UI updates immediately
      setWalletAddress(null);
      setWalletProvider(null);
      setUserId(null);
    } else {
      // Use existing local disconnect logic if no external handler
      if (walletAddress && isConnected) {
        try {
          await unsubscribeFromAddress(walletAddress);
          console.log(`Access: Unsubscribed from wallet ${walletAddress}`);
        } catch (error) {
          console.error("Access: Error unsubscribing from wallet:", error);
        }
      }
      
      // End user session if we have a user ID
      if (userId) {
        try {
          console.log(`Access: Ending session for user ${userId}`);
          await userService.endUserSession(userId);
        } catch (error) {
          console.error("Access: Error ending user session:", error);
        }
      }
      
      // Reset states
      setWallet(null);
      setWalletAddress(null);
      setWalletProvider(null);
    }
  };

  const handleWalletConnected = async (address: string, provider: 'phantom' | 'solflare') => {
    console.log('Access: Wallet connected:', address, provider);
    setWalletAddress(address);
    setWalletProvider(provider);
    
    // Create or update user in database
    const user = await userService.createOrUpdateUser(address, provider, { incrementLoginCount: true });
    if (user) {
      setUserId(user.id);
      console.log('User tracked with ID:', user.id);
    }
    
    if (isConnected) {
      try {
        await subscribeToAddress(address);
        await refreshBalances(address);
        console.log(`Access: Subscribed to wallet updates for ${address}`);
      } catch (error) {
        console.error("Access: Error subscribing to wallet updates:", error);
      }
    }
    
    setShowConnectModal(false);
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 flex flex-col justify-center items-center py-4 relative overflow-hidden">
      {/* Full Background Image */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <img 
          src="https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/213.png"
          alt="Background"
          className="w-full h-full object-cover opacity-20"
        />
      </div>
      
      {/* Architectural Lines */}
      <div className="fixed inset-0 pointer-events-none z-5">
        {/* Horizontal lines */}
        <div className="absolute top-32 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
        <div className="absolute top-1/2 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#24c35b] to-transparent"></div>
        <div className="absolute bottom-32 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
        
        {/* Vertical lines */}
        <div className="absolute top-0 left-1/4 w-0.5 h-full bg-gradient-to-b from-transparent via-gray-200 to-transparent"></div>
        <div className="absolute top-0 right-1/4 w-0.5 h-full bg-gradient-to-b from-transparent via-gray-200 to-transparent"></div>
        
        {/* Cross decorations */}
        <div className="absolute top-32 left-1/4 w-8 h-8 transform -translate-x-1/2 -translate-y-1/2">
          <div className="absolute inset-0 bg-[#24c35b] w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
          <div className="absolute inset-0 bg-[#24c35b] h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
        </div>
        <div className="absolute top-32 right-1/4 w-8 h-8 transform translate-x-1/2 -translate-y-1/2">
          <div className="absolute inset-0 bg-[#24c35b] w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
          <div className="absolute inset-0 bg-[#24c35b] h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
        </div>
        
        {/* Corner plus decorations */}
        <div className="absolute top-8 left-8 w-5 h-5 text-white bg-white rounded-full shadow-lg flex items-center justify-center z-10">
          <div className="w-3 h-3">
            <div className="absolute inset-0 bg-gray-900 w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full"></div>
            <div className="absolute inset-0 bg-gray-900 h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full"></div>
          </div>
        </div>
        <div className="absolute top-8 right-8 w-5 h-5 text-white bg-white rounded-full shadow-lg flex items-center justify-center z-10">
          <div className="w-3 h-3">
            <div className="absolute inset-0 bg-gray-900 w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full"></div>
            <div className="absolute inset-0 bg-gray-900 h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full"></div>
          </div>
        </div>
        <div className="absolute bottom-8 left-8 w-5 h-5 text-white bg-white rounded-full shadow-lg flex items-center justify-center z-10">
          <div className="w-3 h-3">
            <div className="absolute inset-0 bg-gray-900 w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full"></div>
            <div className="absolute inset-0 bg-gray-900 h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full"></div>
          </div>
        </div>
        <div className="absolute bottom-8 right-8 w-5 h-5 text-white bg-white rounded-full shadow-lg flex items-center justify-center z-10">
          <div className="w-3 h-3">
            <div className="absolute inset-0 bg-gray-900 w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full"></div>
            <div className="absolute inset-0 bg-gray-900 h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full"></div>
          </div>
        </div>
      </div>

      {/* Background Text */}
      <div className="fixed inset-0 flex flex-col items-center justify-center pointer-events-none z-1">
        <div className="text-gray-200 font-black select-none text-center space-y-8">
          <div className="text-[12vw] opacity-10 leading-none">ACCESS</div>
          <div className="text-[10vw] opacity-8 leading-none">PLATFORM</div>
          <div className="text-[8vw] opacity-6 leading-none">ENTRY</div>
        </div>
      </div>
      
      {/* Background Glows - now will be visible through transparent components */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-1">
        <div className="absolute top-[-10%] left-[5%] w-[500px] h-[500px] rounded-full bg-green-200/30 blur-[150px]"></div>
        <div className="absolute bottom-[-5%] right-[15%] w-[400px] h-[400px] rounded-full bg-green-200/30 blur-[150px]"></div>
              </div>
              
      <div className="container max-w-6xl mx-auto px-4 z-10 relative">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <img 
            src={EKKO_LOGO}
            alt="EKKO"
            className="h-12 mx-auto mb-4"
          />
        </div>
        
        {/* Compact Header Section */}
        <div className="text-center mb-8 relative z-20">
          <h1 className="text-4xl lg:text-5xl font-black mb-3">
            <span className="bg-gradient-to-r from-[#24c35b] via-[#1eaa4e] to-[#24c35b] bg-clip-text text-transparent">
              ACCESS PLATFORM
            </span>
          </h1>
          <p className="text-lg text-gray-600 mb-6">
            Choose your tier and unlock powerful trading tools
          </p>
          
          {/* Compact Wallet Status */}
          <div className="glassmorphism p-3 rounded-lg border border-white/20 max-w-md mx-auto">
            {walletAddress ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-2 h-2 rounded-full bg-green-500 mr-2 animate-pulse"></div>
                  <span className="text-gray-700 text-sm font-medium">{walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}</span>
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={onLaunchPlatform}
                    className="btn btn-primary btn-md flex items-center gap-1"
                  >
                    Enter <ArrowRight size={14} />
                  </button>
                  <button 
                    onClick={handleDisconnectWallet}
                    className="btn btn-outline btn-md"
                  >
                    Disconnect
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center">
                <button 
                  onClick={handleShowConnectModal}
                  className="btn btn-primary btn-lg flex items-center gap-2"
                >
                  <Wallet size={16} />
                  Connect Wallet
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Clean Tier Grid */}
        <div className="max-w-5xl mx-auto mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {tiers.map((tier, index) => (
              <div 
                key={tier.name}
                className={`relative glassmorphism rounded-lg overflow-hidden transition-all duration-200 cursor-pointer
                  ${activeTier === index 
                    ? 'border-[#24c35b]/60 shadow-[0_0_20px_rgba(36,195,91,0.15)]' 
                    : 'border-white/30 hover:border-[#24c35b]/40'} 
                    border flex flex-col h-full`}
                onClick={() => setActiveTier(index)}
              >
                {/* Status Badge */}
                <div className="absolute top-3 right-3 z-10">
                  {tier.available ? (
                    <div className="px-2 py-1 rounded bg-[#24c35b]/10 text-[#24c35b] text-xs font-bold">
                      ACTIVE
                    </div>
                  ) : (
                    <div className="px-2 py-1 rounded bg-gray-100 text-gray-500 text-xs font-bold">
                      SOON
                    </div>
                  )}
                </div>
                
                {/* Content - grows to fill space */}
                <div className="p-4 flex-1 flex flex-col">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{tier.name}</h3>
                  <p className="text-sm text-gray-600 mb-4">{tier.description}</p>
                  
                  {/* Features - grows to push button down */}
                  <div className="space-y-2 mb-4 flex-1">
                    {tier.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <Check size={14} className="text-[#24c35b] flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Action Button - always at bottom */}
                <div className="p-4 pt-0">
                  {index === 0 ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        walletAddress ? onLaunchPlatform() : handleShowConnectModal();
                      }}
                      className="btn btn-primary btn-lg w-full flex items-center justify-center gap-2"
                    >
                      {walletAddress ? (
                        <>Enter <ArrowRight size={14} /></>
                      ) : (
                        <>Connect <Wallet size={14} /></>
                      )}
                    </button>
                  ) : (
                    <button
                      className="btn btn-secondary btn-lg w-full cursor-not-allowed opacity-50"
                      disabled
                    >
                      Coming Soon
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
          
        {/* Selected Tier Details */}
        {activeTier === 0 ? (
          <div className="max-w-3xl mx-auto relative z-20">
            <div className="glassmorphism rounded-lg border border-white/20 p-6 text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{tiers[activeTier].name}</h2>
              <p className="text-gray-600 mb-4">
                Get early access to EKKO.TRADE's core trading tools. Connect your wallet to start trading on Solana.
              </p>
              
              {walletAddress ? (
                <button 
                  onClick={onLaunchPlatform}
                  className="btn btn-primary btn-lg flex items-center justify-center gap-2 mx-auto"
                >
                  Enter EKKO Platform <ArrowRight size={18} />
                </button>
              ) : (
                <button 
                  onClick={handleShowConnectModal}
                  className="btn btn-primary btn-lg flex items-center justify-center gap-2 mx-auto"
                >
                  <Wallet size={18} />
                  Connect Wallet to Start
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto relative z-20">
            <div className="glassmorphism rounded-lg border border-white/20 p-6 text-center">
              <h3 className="text-xl font-bold text-gray-900 mb-2">This tier is coming soon</h3>
              <p className="text-gray-600 mb-4">
                Access the Beta tier to start trading now
              </p>
              <button 
                className="btn btn-primary btn-lg"
                onClick={() => setActiveTier(0)}
              >
                Go to Beta Access
              </button>
            </div>
          </div>
        )}
      </div>
        
        {/* Connect Wallet Modal - only render if we're managing our own state */}
        {!externalShowConnectModal && (
          <ConnectWallet 
            isOpen={showConnectModal}
            onClose={() => setShowConnectModal(false)}
            onWalletConnected={handleWalletConnected}
            key={`wallet-modal-${showConnectModal}`} // Force re-mount when opened/closed
          />
        )}
    </div>
  );
};

export default Access;
